// ---------------------------------------------------------------------------
// Multi-Monitor / Dual-Screen Types
// ---------------------------------------------------------------------------

/** Logical role assigned to a physical screen */
export type ScreenRole =
  | 'worklist'          // Worklist + report (primary app window)
  | 'primary-viewer'    // Current study primary viewports
  | 'secondary-viewer'  // Current study additional viewports
  | 'prior-viewer'      // Prior / comparison study
  | 'unassigned';

/** Persistent per-screen configuration saved in user preferences */
export interface MonitorRoleConfig {
  /** Screen label from the Window Management API (e.g. "DELL U2723QE") */
  screenLabel: string;
  /** Logical role assigned to this screen */
  role: ScreenRole;
  /** Default viewport grid layout */
  defaultLayout: { rows: number; cols: number };
}

/** Sync categories that can be toggled per-user */
export interface SyncPreferences {
  scroll: boolean;
  windowLevel: boolean;
  tool: boolean;
  crosshair: boolean;
  annotations: boolean;
}

export const DEFAULT_SYNC_PREFERENCES: SyncPreferences = {
  scroll: true,
  windowLevel: true,
  tool: true,
  crosshair: true,
  annotations: true,
};

/** Feature-detection result for the Window Management API */
export interface MultiMonitorCapability {
  /** Browser exposes `window.getScreenDetails` */
  apiSupported: boolean;
  /** User has granted `window-management` permission */
  permissionGranted: boolean;
  /** More than one screen detected */
  multipleScreens: boolean;
  /** Overall: can we open windows on specific screens? */
  available: boolean;
}

/** Runtime state of a single child window */
export interface ChildWindowState {
  /** Unique identifier for this child window */
  windowId: string;
  /** Screen label where this window was opened */
  screenLabel: string;
  /** Assigned role */
  role: ScreenRole;
  /** Reference to the opened Window object */
  windowRef: Window;
  /** Study UID loaded in this window (if any) */
  studyInstanceUID?: string;
  /** Whether the window is still open */
  alive: boolean;
}

// ---------------------------------------------------------------------------
// BroadcastChannel message types (Phase 1 foundation + Phase 2 sync)
// ---------------------------------------------------------------------------

// ── Phase 1: Window lifecycle messages ────────────────────────────────

export interface HeartbeatMessage { type: 'HEARTBEAT'; windowId: string; role: ScreenRole }
export interface WindowOpenedMessage { type: 'WINDOW_OPENED'; windowId: string; role: ScreenRole; screenLabel: string }
export interface WindowClosingMessage { type: 'WINDOW_CLOSING'; windowId: string }
export interface LoadStudyMessage { type: 'LOAD_STUDY'; targetWindowId: string; studyInstanceUID: string; siteId: string }
export interface RequestStateMessage { type: 'REQUEST_STATE'; requestId: string }
export interface StateResponseMessage { type: 'STATE_RESPONSE'; requestId: string; windowId: string; studyInstanceUID?: string }

// ── Phase 2: Cross-window sync messages ──────────────────────────────

export interface ScrollSyncMessage {
  type: 'SCROLL_SYNC';
  sourceWindowId: string;
  seriesInstanceUID: string;
  imageIndex: number;
  /** World position for cross-series spatial sync */
  worldPosition?: [number, number, number];
  /** Frame of reference for spatial matching */
  frameOfReferenceUID?: string;
}

export interface WLSyncMessage {
  type: 'WL_SYNC';
  sourceWindowId: string;
  seriesInstanceUID: string;
  windowCenter: number;
  windowWidth: number;
  voiLutFunction?: string;
}

export interface ToolSyncMessage {
  type: 'TOOL_SYNC';
  sourceWindowId: string;
  toolName: string;
  toolOptions?: Record<string, unknown>;
}

export interface CrosshairSyncMessage {
  type: 'CROSSHAIR_SYNC';
  sourceWindowId: string;
  frameOfReferenceUID: string;
  worldPosition: [number, number, number];
}

export interface AnnotationSyncMessage {
  type: 'ANNOTATION_SYNC';
  sourceWindowId: string;
  action: 'created' | 'updated' | 'deleted';
  annotationUID: string;
  annotation?: unknown; // Serialized cornerstone annotation
  seriesInstanceUID: string;
  sopInstanceUID: string;
}

export interface ResetSyncMessage {
  type: 'RESET_SYNC';
  sourceWindowId: string;
  scope: 'all' | 'viewport';
  viewportId?: string;
  seriesInstanceUID?: string;
}

// ── Phase 3: Multi-screen hanging protocol messages ──────────────────

export interface LoadStudyWithProtocolMessage {
  type: 'LOAD_STUDY_WITH_PROTOCOL';
  targetWindowId: string;
  studyUID: string;
  /**
   * Pre-translated OHIF hanging protocol for this specific window.
   * The primary window performs the translation so child windows don't
   * need the full BritProtocol translation machinery.
   */
  ohifProtocol: SerializedOHIFProtocol;
}

/**
 * Serializable subset of OHIFHangingProtocol for BroadcastChannel transfer.
 * Matches the shape of OHIFHangingProtocol from britToOhif.ts but is
 * defined independently to avoid a circular import.
 */
export interface SerializedOHIFProtocol {
  id: string;
  locked: boolean;
  name: string;
  createdDate: string;
  modifiedDate: string;
  availableTo: Record<string, never>;
  editableBy: Record<string, never>;
  protocolMatchingRules: Array<{
    id?: string;
    weight: number;
    attribute: string;
    constraint: Record<string, unknown>;
    required?: boolean;
  }>;
  stages: Array<{
    id: string;
    name: string;
    viewportStructure: {
      layoutType: string;
      properties: { rows: number; columns: number };
    };
    viewports: Array<{
      viewportOptions: { viewportType: string; toolGroupId: string };
      displaySets: Array<{
        id?: string;
        seriesMatchingRules?: Array<{
          id?: string;
          weight: number;
          attribute: string;
          constraint: Record<string, unknown>;
          required?: boolean;
        }>;
        initialImageOptions?: { index: number };
      }>;
      displaySetOptions?: Array<{
        voi?: { windowWidth: number; windowCenter: number };
      }>;
    }>;
  }>;
  numberOfPriorsReferenced: number;
}

// ── Union of all channel messages ────────────────────────────────────

export type MonitorChannelMessage =
  | HeartbeatMessage
  | WindowOpenedMessage
  | WindowClosingMessage
  | LoadStudyMessage
  | RequestStateMessage
  | StateResponseMessage
  | ScrollSyncMessage
  | WLSyncMessage
  | ToolSyncMessage
  | CrosshairSyncMessage
  | AnnotationSyncMessage
  | ResetSyncMessage
  | LoadStudyWithProtocolMessage;

/** Channel name for all multi-monitor communication */
export const MONITOR_CHANNEL_NAME = 'brit-multi-monitor';

/** Interval (ms) between heartbeat pings */
export const HEARTBEAT_INTERVAL_MS = 3000;

/** How long before a window is considered dead (no heartbeat) */
export const HEARTBEAT_TIMEOUT_MS = 10000;

// ---------------------------------------------------------------------------
// Phase 3: Multi-screen hanging protocol types
//
// Protocol distribution logic lives in viewer/protocols/protocolDistributor.ts.
// The types below supported an earlier draft design and are retained only
// for backward compatibility if any consumer still references them.
// New code should use `distributeProtocol()` from protocolDistributor.ts
// which works directly with the backend `BritProtocol` type.
// ---------------------------------------------------------------------------

/**
 * @deprecated Use `isMultiScreenBritProtocol()` from britToOhif.ts instead.
 * Retained for backward compatibility with existing consumers.
 */
export interface MultiScreenProtocol {
  protocolId: string;
  name: string;
  screens?: Array<{ screenRole: ScreenRole; label?: string }>;
}

/**
 * @deprecated Use `isMultiScreenBritProtocol()` from britToOhif.ts instead.
 */
export function isMultiScreenProtocol(protocol: MultiScreenProtocol): boolean {
  return Array.isArray(protocol.screens) && protocol.screens.length > 0;
}

// ---------------------------------------------------------------------------
// Phase 4: Persisted monitor configuration
// ---------------------------------------------------------------------------

export interface PersistedMonitorConfig {
  version: 1;
  updatedAt: string;
  screens: Array<{
    screenLabel: string;
    role: ScreenRole;
    defaultLayout: { rows: number; cols: number };
  }>;
  syncPreferences: SyncPreferences;
}
